import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl'
import { DAYS, DAY_HUMAN_LONG_FORMAT, HOUR_FORMAT_WITH_SECONDS, LOCALE_TIME_FORMAT } from 'commons/js/constants'
import { parseMoment, formatDate } from 'commons/js/util/dateConverter'
import { Box, Copy } from '@elparking/components'

const messages = defineMessages({
    fromTo: {
        defaultMessage: '{startDate} a {endDate}',
    },
})

const formatHour = (h) => formatDate(parseMoment(h, HOUR_FORMAT_WITH_SECONDS), LOCALE_TIME_FORMAT)

export const getResume = (timeTables) => {
    if (!timeTables) {
        return null
    }
    const resume = []
    DAYS.forEach((day) => {
        if (timeTables[`availability_${day}`]) {
            resume.push({name: day, hours: timeTables[day], isOpen24h: timeTables[`${day}_24h`]})
        }
    })
    return resume
}

const TimeInfo = ({intl, hours, isOpen24h}) => {
    if (isOpen24h) {
        return (
            <FormattedMessage
              defaultMessage='24h'
            />
        )
    }

    return hours.map(({ start, end }) => {
        if (start && end) {
            return intl.formatMessage(messages.fromTo, {
                startDate: formatHour(start),
                endDate: formatHour(end),
            })
        }
        return null
    })
    .filter((item) => !!item)
    .join(' | ')
}

TimeInfo.propTypes = {
    intl: PropTypes.object,
    isOpen24h: PropTypes.bool,
    hours: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.string,
            end: PropTypes.string,
        })
    ),
}

const getIndexFromDayName = (dayName) => {
    return DAYS.indexOf(dayName)
}

const reduceResume = (resume) => {
    if (!resume) {
        return null
    }
    return resume.reduce((total, { hours, isOpen24h, name }) => {
        if (total.length > 0) {
            const lastItem = total[total.length - 1]
            if (JSON.stringify(hours) === JSON.stringify(lastItem.hours)) {
                lastItem['endDay'] = getIndexFromDayName(name) + 1
                return total
            }
        }
        return [
            ...total,
            {
                hours,
                startDay: getIndexFromDayName(name) + 1,
                endDay: null,
                isOpen24h,
            },
        ]
    }, []).sort((tta, ttb) => tta.startDay - ttb.startDay)
}

const getDayName = (day) => moment()
    .day(day)
    .format(DAY_HUMAN_LONG_FORMAT)

    const Timetable = ({ timetables, intl, ...restProps }) => {
    const timeTable = useMemo(() => reduceResume(getResume(timetables)), [timetables])
    if (!timetables) {
        return <Copy as='span' padding='0' fontWeight='book' {...restProps}>
            <FormattedMessage defaultMessage='No hay información del horario' />
        </Copy>
    }

    return timeTable.map(({ startDay: s, endDay, hours, isOpen24h }, index) => {
        const startDay = getDayName(s).charAt(0).toUpperCase() + getDayName(s).slice(1)
        return (
            <Box key={`timetable_${index}`}>
                <Copy as='span' padding='0' fontWeight='book' {...restProps}>
                    {endDay
                        ? <FormattedMessage defaultMessage='{startDate} a {endDate}'
                            values={{
                              startDate: startDay,
                              endDate: getDayName(endDay),
                          }} />
                        : startDay}
                    {' '}
                    <TimeInfo intl={intl} isOpen24h={isOpen24h} hours={hours} />
                </Copy>
            </Box>
        )
    })
}

Timetable.propTypes = {
    intl: PropTypes.object,
    timetables: PropTypes.object,
}

export default injectIntl(Timetable)
